import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppMenuRules } from "./AppMenuRules";
export { AppMenuRules } from "./AppMenuRules";

// AppMenu - ONE BEHAVIOUR FOR EVERY POP-UP MENU (design items 7 + 9, 2026-09-30): the top bar's settings and help, the chat
// header's "⋯" (Export chat, translation). A menu is markup only - a button `[data-wac-menu="<popover id>"]` with
// `aria-haspopup`, `aria-expanded` and `aria-controls`, and its popover `#<id>` (`hidden` while shut). This class gives them:
//   - click on the button: open (every other menu shuts) / shut;
//   - Esc: shut and give focus back to the button; Tab or a click anywhere else: shut;
//   - ArrowDown / ArrowUp / Home / End: move between the popover's buttons (ArrowDown on a shut button opens it);
//   - a click on a button inside a menu (not a dialog): that item's own handler runs, then the menu shuts.
// ⭐ LISTENS ONCE ON THE DOCUMENT (like `AppThemeRun`): the views are repainted with innerHTML, and a listener on the
// buttons themselves would be lost with them. ⛔ Nothing here sends (decision 9) - an item's own handler does its job.
// ⚠ SHAPED BY INTERFACES so the suite drives it with a fake document.

export interface IMenuEl {
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
    hasAttribute(Name: string): boolean;
    focus(): void;
    contains(Other: IMenuEl | null): boolean;
    closest(Selector: string): IMenuEl | null;
    querySelectorAll(Selector: string): ArrayLike<IMenuEl>;
}

export interface IMenuDoc {
    getElementById(Id: string): IMenuEl | null;
    querySelectorAll(Selector: string): ArrayLike<IMenuEl>;
}

export interface IMenuEvent {
    target: unknown;
    key?: string;
    preventDefault(): void;
}

export class AppMenus {
    private Doc: IMenuDoc;

    public constructor(Doc: IMenuDoc) {
        this.Doc = Doc;
    }

    private static Page: AppMenus | null = null;

    /** The page's one instance, listening on the document (once). */
    public static ForPage = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppMenus.Page !== null || typeof document === "undefined") { return; }
            const Made: AppMenus = new AppMenus(document as unknown as IMenuDoc);
            document.addEventListener("click", Made.OnClick);
            document.addEventListener("keydown", Made.OnKey);
            document.addEventListener("focusin", Made.OnFocus);
            AppMenus.Page = Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.ForPage");
        }
    };

    /** The element an event happened on, when it is one (a text node's parent is not needed: `closest` covers it). */
    private static TargetOf = (Evt: IMenuEvent): IMenuEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Target: unknown = Evt.target;
            if (Target === null || typeof Target !== "object" || typeof (Target as IMenuEl).closest !== "function") { return null; }
            return Target as IMenuEl;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.TargetOf");
            return null;
        }
    };

    public OnClick = (Evt: IMenuEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: IMenuEl | null = AppMenus.TargetOf(Evt);
            let Button: IMenuEl | null = null;
            if (Target !== null) { Button = Target.closest("[" + AppMenuRules.Attr + "]"); }
            if (Button !== null) { this.Toggle(Button); return; }
            const Open: IMenuEl | null = this.OpenButton();
            if (Open === null) { return; }
            const Pop: IMenuEl | null = this.PopOf(Open);
            if (Pop !== null && Target !== null && Pop.contains(Target) === true) {
                if (nz(Pop.getAttribute("role")) === "dialog" || Target.closest("button") === null) { return; }
                this.Close(Open, true);
                return;
            }
            this.Close(Open, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.OnClick");
        }
    };

    public OnKey = (Evt: IMenuEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Key: string = nz(Evt.key);
            const Target: IMenuEl | null = AppMenus.TargetOf(Evt);
            const Open: IMenuEl | null = this.OpenButton();
            if (Key === "Escape" && Open !== null) { Evt.preventDefault(); this.Close(Open, true); return; }
            const Button: IMenuEl | null = Open;
            if (Button === null && Target !== null && Target.hasAttribute(AppMenuRules.Attr) === true && Key === "ArrowDown") {
                Evt.preventDefault();
                this.Open(Target);
                return;
            }
            if (Button === null || Target === null) { return; }
            const Items: IMenuEl[] = this.Items(Button);
            const At: number = AppMenus.IndexOf(Items, Target);
            const Next: number = AppMenuRules.Move(At, Items.length, Key);
            if (Next < 0 || (At < 0 && Target !== Button)) { return; }
            Evt.preventDefault();
            Items[Next].focus();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.OnKey");
        }
    };

    /** Focus left the open menu (Tab, a click that moved focus): it shuts, focus stays where it went. */
    public OnFocus = (Evt: IMenuEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Open: IMenuEl | null = this.OpenButton();
            const Target: IMenuEl | null = AppMenus.TargetOf(Evt);
            if (Open === null || Target === null || Target === Open) { return; }
            const Pop: IMenuEl | null = this.PopOf(Open);
            if (Pop !== null && Pop.contains(Target) === true) { return; }
            this.Close(Open, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.OnFocus");
        }
    };

    public Toggle = (Button: IMenuEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (nz(Button.getAttribute("aria-expanded")) === "true") { this.Close(Button, false); return; }
            this.Open(Button);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.Toggle");
        }
    };

    /** Open one menu (others shut), items named for a screen reader, focus on the first item of a menu. */
    public Open = (Button: IMenuEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Other: IMenuEl | null = this.OpenButton();
            if (Other !== null && Other !== Button) { this.Close(Other, false); }
            const Pop: IMenuEl | null = this.PopOf(Button);
            if (Pop === null) { return; }
            Pop.removeAttribute("hidden");
            Button.setAttribute("aria-expanded", "true");
            const Items: IMenuEl[] = this.Items(Button);
            for (const One of Items) { AppMenus.NameItem(One, nz(Pop.getAttribute("role"))); }
            if (Items.length > 0 && nz(Pop.getAttribute("role")) === "menu") { Items[0].focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.Open");
        }
    };

    public Close = (Button: IMenuEl, Refocus: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Pop: IMenuEl | null = this.PopOf(Button);
            if (Pop !== null) { Pop.setAttribute("hidden", ""); }
            Button.setAttribute("aria-expanded", "false");
            if (Refocus === true) { Button.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.Close");
        }
    };

    /** The one open menu's button, or null. */
    public OpenButton = (): IMenuEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const All: ArrayLike<IMenuEl> = this.Doc.querySelectorAll("[" + AppMenuRules.Attr + "][aria-expanded=\"true\"]");
            if (All.length === 0) { return null; }
            return All[0];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.OpenButton");
            return null;
        }
    };

    private PopOf = (Button: IMenuEl): IMenuEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            return this.Doc.getElementById(nz(Button.getAttribute(AppMenuRules.Attr)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.PopOf");
            return null;
        }
    };

    /** The popover's usable buttons, in order (disabled and hidden ones are skipped). */
    public Items = (Button: IMenuEl): IMenuEl[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Pop: IMenuEl | null = this.PopOf(Button);
            if (Pop === null) { return []; }
            const All: ArrayLike<IMenuEl> = Pop.querySelectorAll("button");
            const Out: IMenuEl[] = [];
            for (let At: number = 0; At < All.length; At = At + 1) {
                if (All[At].hasAttribute("disabled") === true || All[At].hasAttribute("hidden") === true) { continue; }
                Out.push(All[At]);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.Items");
            return [];
        }
    };

    private static IndexOf = (Items: IMenuEl[], Target: IMenuEl): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            for (let At: number = 0; At < Items.length; At = At + 1) {
                if (Items[At] === Target || Items[At].contains(Target) === true) { return At; }
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.IndexOf");
            return -1;
        }
    };

    /** In a `menu`, an item with no role becomes a `menuitem`; a radio item's `aria-checked` follows its `aria-pressed`. */
    private static NameItem = (Item: IMenuEl, PopRole: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (PopRole !== "menu") { return; }
            const Role: string = nz(Item.getAttribute("role"));
            if (Role.length === 0) { Item.setAttribute("role", "menuitem"); }
            // ⛔ A radio item says its state with `aria-checked` only: one written with `aria-pressed` (not allowed on the role)
            // is converted, and an item already carrying `aria-checked` is left as it is.
            if (Role === "menuitemradio" && Item.getAttribute("aria-pressed") !== null) {
                Item.setAttribute("aria-checked", nz(Item.getAttribute("aria-pressed")) || "false");
                Item.removeAttribute("aria-pressed");
            }
            if (Role === "menuitemradio" && Item.getAttribute("aria-checked") === null) { Item.setAttribute("aria-checked", "false"); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMenus.NameItem");
        }
    };
}
